/*
 * Nexo header behaviour:
 * - <nexo-search>: live product suggestions (Section Rendering API), keyboard navigation (↑ ↓ Enter
 *   Esc). In the header (desktop) the suggestions drop down and close on an outside click; inside the
 *   full-screen search (data-mode="sheet") they fill the panel, which also shows recent searches.
 * - <nexo-search-sheet>: full-screen search on phones, opened by any [data-nexo-search-open]
 *   (the magnifier next to the cart, and the home page's bar).
 * - <nexo-drawer>: native <dialog> side panel, opened by any [data-nexo-open="<dialog id>"].
 * - Keeps --header-height / --header-group-height up to date for Horizon's sticky elements.
 * - Phones: hides the department pills once the page is scrolled past COMPACT_AT (html.nx-hdr-compact),
 *   and shows them again back above EXPAND_AT. The gap between both avoids flicker.
 * - Browser bar colour (theme-color) follows the top of the page; passive touchstart so iOS shows
 *   the :active press states.
 * Custom elements so everything re-initialises when the theme editor re-renders the section.
 */

const phoneQuery = window.matchMedia('(max-width: 749px)');

/* Recent searches, kept on the shopper's device. Storage can throw (private browsing, blocked
   site data): every access is wrapped and the feature just stays empty. */
const RECENT_KEY = 'nx-recent-searches';
const RECENT_MAX = 5;

const recentSearches = {
  read() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((s) => typeof s === 'string' && s).slice(0, RECENT_MAX) : [];
    } catch {
      return [];
    }
  },
  write(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch {
      /* not available: nothing to keep */
    }
  },
  add(term) {
    const q = String(term || '').trim().replace(/\s+/g, ' ');
    if (q.length < 2) return;
    const key = q.toLowerCase();
    this.write([q, ...this.read().filter((s) => s.toLowerCase() !== key)]);
  },
  remove(term) {
    this.write(this.read().filter((s) => s !== term));
  },
  clear() {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {
      /* not available */
    }
  },
};

class NexoSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('.nx-suggest');
    if (!this.input || !this.panel) return;
    this.sheet = this.dataset.mode === 'sheet';
    this.host = this.sheet ? this.closest('dialog') : null;
    this.status = this.querySelector('[data-nx-status]');
    this.clearBtn = this.querySelector('[data-nx-clear]');
    this.form = this.input.form;
    this.active = -1;
    this.lastQ = '';
    this.lastState = 'idle';
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('focus', this.onFocus);
    this.form?.addEventListener('submit', this.onSubmit);
    this.panel.addEventListener('click', this.onPick);
    if (this.sheet) {
      this.addEventListener('click', this.onSheetClick);
      this.syncClear();
      this.renderRecent();
    } else {
      document.addEventListener('pointerdown', this.onOutside);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutside);
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  onInput = () => {
    clearTimeout(this.timer);
    this.syncClear();
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.controller?.abort();
      this.close();
      this.setState('idle');
      return;
    }
    this.timer = setTimeout(() => this.load(q), 180);
  };

  onFocus = () => {
    if (this.sheet) return;
    if (this.input.value.trim().length >= 2 && this.panel.children.length) this.open();
  };

  onOutside = (event) => {
    if (!this.contains(event.target)) this.close();
  };

  onSubmit = (event) => {
    const q = this.input.value.trim();
    if (this.sheet && !q) {
      // Nothing typed: stay in the panel instead of opening an empty results page
      event.preventDefault();
      return;
    }
    recentSearches.add(q);
  };

  // A suggestion or "Ver todos" was chosen: remember what was typed (the link then navigates)
  onPick = (event) => {
    if (event.target.closest('a[role="option"]')) recentSearches.add(this.input.value);
  };

  onSheetClick = (event) => {
    const term = event.target.closest('[data-nx-term]');
    if (term) {
      this.useTerm(term.dataset.nxTerm);
      return;
    }
    const remove = event.target.closest('[data-nx-recent-remove]');
    if (remove) {
      recentSearches.remove(remove.dataset.nxRecentRemove);
      this.renderRecent();
      return;
    }
    if (event.target.closest('[data-nx-recent-clear]')) {
      recentSearches.clear();
      this.renderRecent();
      return;
    }
    if (event.target.closest('[data-nx-clear]')) {
      this.input.value = '';
      this.onInput();
      this.input.focus({ preventScroll: true });
    }
  };

  // Recent or popular search tapped: fill the field and search right away
  useTerm(term) {
    const q = String(term || '').trim();
    if (!q) return;
    this.input.value = q;
    this.syncClear();
    clearTimeout(this.timer);
    this.load(q);
  }

  // Called each time the full-screen search opens
  refresh() {
    this.syncClear();
    this.renderRecent();
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.close();
      this.setState('idle');
    } else if (q === this.lastQ && this.panel.children.length) {
      this.open();
      this.setState(this.lastState);
    } else {
      this.load(q);
    }
  }

  syncClear() {
    if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
  }

  setState(state) {
    if (this.host) this.host.dataset.state = state;
  }

  renderRecent() {
    const section = this.querySelector('[data-nx-recent]');
    const list = section?.querySelector('[data-nx-recent-list]');
    const tpl = section?.querySelector('[data-nx-recent-tpl]');
    if (!section || !list || !tpl?.content.firstElementChild) return;
    const items = recentSearches.read();
    list.replaceChildren(
      ...items.map((q) => {
        const row = tpl.content.firstElementChild.cloneNode(true);
        const use = row.querySelector('[data-nx-term]');
        const remove = row.querySelector('[data-nx-recent-remove]');
        if (use) {
          use.dataset.nxTerm = q;
          const label = use.querySelector('span');
          if (label) label.textContent = q;
        }
        if (remove) {
          remove.dataset.nxRecentRemove = q;
          remove.setAttribute('aria-label', `Quitar “${q}” de las búsquedas recientes`);
        }
        return row;
      })
    );
    section.hidden = items.length === 0;
  }

  async load(q) {
    this.controller?.abort();
    this.controller = new AbortController();
    const url = new URL(this.dataset.url || '/search/suggest', location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('section_id', this.dataset.sectionId || 'nexo-predictive-search');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.sku,tag');

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      if (q !== this.input.value.trim()) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.nx-suggest__inner');
      // The header dropdown and the full-screen search can both hold suggestions: keep ids unique
      if (inner && this.sheet) inner.querySelectorAll('[id]').forEach((el) => (el.id = `sheet-${el.id}`));
      this.panel.replaceChildren(...(inner ? [inner] : []));
      this.active = -1;
      if (!inner) {
        this.close();
        this.setState('idle');
        return;
      }
      const none = !!inner.querySelector('.nx-suggest__none');
      this.lastQ = q;
      this.lastState = none ? 'none' : 'results';
      this.open();
      this.setState(this.lastState);
      this.announce(none ? 'Sin resultados' : this.countLabel(inner));
      if (none) this.updateWhatsApp(q);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.close();
        this.setState('idle');
      }
    }
  }

  countLabel(inner) {
    const n = inner.querySelectorAll('.nx-suggest__item').length;
    return `${n} ${n === 1 ? 'producto' : 'productos'}`;
  }

  announce(text) {
    if (this.status) this.status.textContent = text;
  }

  // No results: the WhatsApp card starts the chat with what the shopper was looking for
  updateWhatsApp(q) {
    const link = this.querySelector('[data-nx-wa]');
    if (!link) return;
    link.href = `${link.dataset.nxWa}?text=${encodeURIComponent(`Hola, estoy buscando: ${q}`)}`;
  }

  get options() {
    // Visible rows only: the department row is hidden in the desktop dropdown
    return [...this.panel.querySelectorAll('[role="option"]')].filter((o) => o.getClientRects().length > 0);
  }

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      // In the full-screen search Esc closes the whole panel (the dialog's own cancel)
      if (!this.sheet) this.close();
      return;
    }
    const options = this.options;
    if (this.panel.hidden || !options.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const n = options.length;
      this.active = event.key === 'ArrowDown' ? (this.active + 1) % n : (this.active - 1 + n) % n;
      this.mark(options);
    } else if (event.key === 'Enter' && this.active > -1) {
      event.preventDefault();
      options[this.active].click();
    }
  };

  mark(options) {
    options.forEach((option, i) => {
      const on = i === this.active;
      option.classList.toggle('is-active', on);
      option.setAttribute('aria-selected', String(on));
    });
    const current = options[this.active];
    if (current) {
      this.input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    }
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }
}

/*
 * Full-screen search on phones. A native <dialog>: focus stays inside, Esc closes it, and Chrome on
 * Android closes it with the back button. No history entry on purpose: stepping back over one fires
 * popstate, and the collection and product scripts reload their content on popstate.
 */
class NexoSearchSheet extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.search = this.querySelector('nexo-search');
    if (!this.dialog || !this.search) return;
    this.input = this.dialog.querySelector('input[type="search"]');
    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('close', this.onClosed);
    phoneQuery.addEventListener('change', this.onMedia);
  }

  disconnectedCallback() {
    phoneQuery.removeEventListener('change', this.onMedia);
    this.unwatchViewport();
    // Theme editor re-render with the panel open: the dialog goes away without a close event
    if (this.isOpen) {
      this.isOpen = false;
      document.documentElement.classList.remove('nx-lock');
      this.restoreBarColor();
    }
  }

  open(trigger) {
    if (!this.dialog || this.dialog.open) return;
    this.trigger = trigger;
    this.dialog.showModal();
    // In the same tap as the trigger: iOS only shows the keyboard for a focus() inside the gesture
    this.input?.focus({ preventScroll: true });
    this.isOpen = true;
    document.documentElement.classList.add('nx-lock');
    this.setBarColor('#ffffff');
    this.watchViewport();
    this.search.refresh?.();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  onClick = (event) => {
    if (event.target.closest('[data-nexo-close]')) this.close();
  };

  onMedia = () => {
    if (!phoneQuery.matches) this.close();
  };

  // Every way of closing ends here: close button, Esc, Android back button, wider screen
  onClosed = () => {
    this.isOpen = false;
    document.documentElement.classList.remove('nx-lock');
    this.restoreBarColor();
    this.unwatchViewport();
    this.trigger?.focus({ preventScroll: true });
  };

  /* The panel keeps its full height and its content runs under the keyboard, as in native iOS apps
     (shrinking it to the visible area cut the list off at a hard edge above the keyboard).
     --nx-kb is how much of the screen the keyboard covers: bottom padding, so the last row can
     still scroll above it. --nx-vvt keeps the field on top if iOS shifts the visible area. */
  watchViewport() {
    const vv = window.visualViewport;
    if (!vv || this.onViewport) return;
    this.onViewport = () => {
      const layout = Math.max(window.innerHeight, document.documentElement.clientHeight);
      const covered = Math.max(0, Math.round(layout - vv.height - vv.offsetTop));
      this.dialog.style.setProperty('--nx-kb', `${covered}px`);
      this.dialog.style.setProperty('--nx-vvt', `${vv.offsetTop}px`);
    };
    this.onViewport();
    vv.addEventListener('resize', this.onViewport);
    vv.addEventListener('scroll', this.onViewport);
  }

  unwatchViewport() {
    const vv = window.visualViewport;
    if (vv && this.onViewport) {
      vv.removeEventListener('resize', this.onViewport);
      vv.removeEventListener('scroll', this.onViewport);
    }
    this.onViewport = null;
    this.dialog?.style.removeProperty('--nx-kb');
    this.dialog?.style.removeProperty('--nx-vvt');
  }

  // Browser bar colour: white while the panel covers the page, back to what it was after
  setBarColor(color) {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    this.prevColor = meta.getAttribute('content');
    meta.setAttribute('content', color);
  }

  restoreBarColor() {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && this.prevColor != null) meta.setAttribute('content', this.prevColor);
    this.prevColor = null;
  }
}

class NexoDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.dialog.addEventListener('close', this.onClose);
  }

  open(trigger) {
    if (!this.dialog || this.dialog.open) return;
    this.trigger = trigger;
    this.dialog.showModal();
    document.documentElement.classList.add('nx-lock');
  }

  /** Slides the panel out and fades the backdrop (nexo.css), then closes the dialog. */
  close() {
    const dialog = this.dialog;
    if (!dialog?.open || this.closing) return;
    this.closing = true;
    dialog.classList.add('nx-drawer--closing');
    // The panel's transition and its ::backdrop fade; not animations inside the menu
    const animations = dialog
      .getAnimations({ subtree: true })
      .filter((animation) => animation.effect instanceof KeyframeEffect && animation.effect.target === dialog);
    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => dialog.close());
  }

  // Escape, and the Android back button: animate instead of the instant native close
  onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  // Runs after the slide-out, or right away if the browser closed the dialog on its own. The page
  // scroll is unlocked here, once the panel is gone (unlocking re-lays out the whole page).
  onClose = () => {
    this.closing = false;
    this.dialog.classList.remove('nx-drawer--closing');
    document.documentElement.classList.remove('nx-lock');
    this.trigger?.focus({ preventScroll: true });
  };

  onClick = (event) => {
    // Backdrop click (the dialog itself is the target) or a close button
    if (event.target === this.dialog || event.target.closest('[data-nexo-close]')) this.close();
  };
}

if (!customElements.get('nexo-search')) customElements.define('nexo-search', NexoSearch);
if (!customElements.get('nexo-search-sheet')) customElements.define('nexo-search-sheet', NexoSearchSheet);
if (!customElements.get('nexo-drawer')) customElements.define('nexo-drawer', NexoDrawer);

if (!window.nexoHeaderReady) {
  window.nexoHeaderReady = true;

  document.addEventListener('click', (event) => {
    // Phones: the magnifier and the home page's bar open the full-screen search. Without the panel
    // (or on wider screens) the magnifier stays a plain link to /search.
    const searchTrigger = event.target.closest('[data-nexo-search-open]');
    if (searchTrigger) {
      const sheet = document.querySelector('nexo-search-sheet');
      if (sheet?.dialog && phoneQuery.matches) {
        event.preventDefault();
        sheet.open(searchTrigger);
      }
      return;
    }

    const trigger = event.target.closest('[data-nexo-open]');
    if (!trigger) return;
    const dialog = document.getElementById(trigger.dataset.nexoOpen);
    const drawer = dialog?.closest('nexo-drawer');
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  const COMPACT_AT = 320;
  const EXPAND_AT = 140;
  const phone = phoneQuery;
  const root = document.documentElement;

  const chipsHeight = () => {
    const chips = document.querySelector('.nx-hdr .nx-chips');
    return chips && phone.matches ? chips.offsetHeight : 0;
  };

  const setHeights = () => {
    const header = document.querySelector('.nx-header-section');
    const group = document.getElementById('header-group');
    if (!header) return;
    const chips = chipsHeight();
    root.style.setProperty('--nx-chips-h', `${chips}px`);
    // Sticky elements below the header should line up with what is visible of it
    const visible = header.offsetHeight - (root.classList.contains('nx-hdr-compact') ? chips : 0);
    let groupHeight = 0;
    group?.querySelectorAll(':scope > .shopify-section').forEach((s) => (groupHeight += s.offsetHeight));
    document.body.style.setProperty('--header-height', `${visible}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight || header.offsetHeight}px`);
  };

  let ticking = false;
  const updateCompact = () => {
    ticking = false;
    const y = window.scrollY;
    const compact = root.classList.contains('nx-hdr-compact');
    let next = compact;
    if (!phone.matches || chipsHeight() === 0) next = false;
    else if (!compact && y > COMPACT_AT) next = true;
    else if (compact && y < EXPAND_AT) next = false;
    if (next !== compact) {
      root.classList.toggle('nx-hdr-compact', next);
      setHeights();
    }
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateCompact);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  phone.addEventListener('change', () => {
    updateCompact();
    setHeights();
  });

  // Home page on phones: the bar is covered by a button that opens the full-screen search, so the
  // field under it leaves the tab order and the accessibility tree (one "Buscar productos", not two)
  const syncHomeField = () => {
    const field = document.querySelector('.nx-hdr--home .nx-search input[type="search"]');
    if (!field) return;
    if (phone.matches) {
      field.setAttribute('tabindex', '-1');
      field.setAttribute('aria-hidden', 'true');
    } else {
      field.removeAttribute('tabindex');
      field.removeAttribute('aria-hidden');
    }
  };
  syncHomeField();
  phone.addEventListener('change', syncHomeField);
  document.addEventListener('shopify:section:load', syncHomeField);

  // iOS applies :active (the press feedback in nexo.css) only when a touchstart listener exists
  document.addEventListener('touchstart', () => {}, { passive: true });

  // Browser bar colour (theme-color; Android Chrome, some Safari versions): the colour at the top
  // of the page, the dark message bar while it's on screen and the header once it scrolls away
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) {
    const solid = (el) => {
      const bg = el ? getComputedStyle(el).backgroundColor : '';
      return bg && bg !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(bg) ? bg : '';
    };
    const headerColor = () =>
      solid(document.querySelector('.nx-hdr')) || solid(document.querySelector('.nx-header-section')) || '#ffffff';
    const ticker = document.querySelector('.nx-ticker');
    const tickerColor = solid(ticker);
    if (ticker && tickerColor && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        // While the full-screen search is open it owns the colour (it restores it on close)
        if (document.querySelector('.nx-ssheet[open]')) return;
        themeMeta.setAttribute('content', entry.isIntersecting ? tickerColor : headerColor());
      }).observe(ticker);
    } else {
      themeMeta.setAttribute('content', headerColor());
    }
  }

  setHeights();
  updateCompact();
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(setHeights);
    const watch = () => document.querySelectorAll('#header-group > .shopify-section').forEach((s) => observer.observe(s));
    watch();
    document.addEventListener('shopify:section:load', () => {
      watch();
      setHeights();
    });
  }
}
